import { Box, Flex } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import * as React from 'react';
import { type ThemeName, themes } from '../foundation/tokens';

const STORAGE_KEY = 'cy-theme';
const NAMES: ThemeName[] = ['white', 'cream', 'dark'];

const vars = (t: ThemeName) =>
  Object.entries(themes[t])
    .map(([k, val]) => `--cy-${k}: ${val};`)
    .join(' ');

/** CSS variables for every theme, plus dark-mode fixes for artwork. */
export const ThemeStyles: React.FC = () => (
  <Global
    styles={`
      /* Locally bundled to keep typography reliable and avoid third-party
         font requests on every page. */
      @font-face { font-family: 'Figtree'; src: url('/fonts/Figtree-400.woff2') format('woff2'), url('/fonts/Figtree-400.ttf') format('truetype'); font-style: normal; font-weight: 400; font-display: swap; }
      @font-face { font-family: 'Figtree'; src: url('/fonts/Figtree-500.woff2') format('woff2'), url('/fonts/Figtree-500.ttf') format('truetype'); font-style: normal; font-weight: 500; font-display: swap; }
      @font-face { font-family: 'Figtree'; src: url('/fonts/Figtree-600.woff2') format('woff2'), url('/fonts/Figtree-600.ttf') format('truetype'); font-style: normal; font-weight: 600; font-display: swap; }
      @font-face { font-family: 'Figtree'; src: url('/fonts/Figtree-700.woff2') format('woff2'), url('/fonts/Figtree-700.ttf') format('truetype'); font-style: normal; font-weight: 700; font-display: swap; }
      @font-face { font-family: 'Figtree'; src: url('/fonts/Figtree-800.woff2') format('woff2'), url('/fonts/Figtree-800.ttf') format('truetype'); font-style: normal; font-weight: 800; font-display: swap; }
      @font-face { font-family: 'Playfair Display'; src: url('/fonts/PlayfairDisplay-700.woff2') format('woff2'), url('/fonts/PlayfairDisplay-700.ttf') format('truetype'); font-style: normal; font-weight: 700; font-display: swap; }
      @font-face { font-family: 'Playfair Display'; src: url('/fonts/PlayfairDisplay-800.woff2') format('woff2'), url('/fonts/PlayfairDisplay-800.ttf') format('truetype'); font-style: normal; font-weight: 800; font-display: swap; }
      @font-face { font-family: 'Canyou Dollar'; src: url('/fonts/Figtree-700.woff2') format('woff2'), url('/fonts/Figtree-700.ttf') format('truetype'); font-style: normal; font-weight: 700; font-display: swap; unicode-range: U+0024; }
      @font-face { font-family: 'Canyou Dollar'; src: url('/fonts/Figtree-800.woff2') format('woff2'), url('/fonts/Figtree-800.ttf') format('truetype'); font-style: normal; font-weight: 800; font-display: swap; unicode-range: U+0024; }
      :root { ${vars('white')} }
      html[data-cy-theme='cream'] { ${vars('cream')} }
      html[data-cy-theme='dark'] { ${vars('dark')} color-scheme: dark; }
      html[data-cy-theme] body { background: var(--cy-page); }
      /* Anchor jumps land below the sticky nav, not under it. */
      [id] { scroll-margin-top: 96px; }
      /* One keyboard focus ring for every interactive element. */
      :where(a, button, summary, input, select, textarea, [role='radio'],
        [role='tab'], [tabindex]):focus-visible {
        outline: 3px solid #2667FF;
        outline-offset: 3px;
        border-radius: 6px;
      }
      html[data-cy-theme='dark'] :where(a, button, summary, input, select,
        textarea, [role='radio'], [role='tab'], [tabindex]):focus-visible {
        outline-color: #8AAEFF;
      }
      /* Dark theme: cards and containers stay white, so everything inside a
         white surface switches back to the light palette. */
      html[data-cy-theme='dark'] .cy-surface {
        ${vars('white')}
        color: ${themes.white.ink};
      }
      /* Apollo's fills are transparent and his linework is black, so he
         always sits on white (per the rollout guide). */
      html[data-cy-theme='dark'] .cy-apollo {
        background: #fff;
        border-radius: 16px;
        padding: 6px;
      }
      /* Dark-text partner logos need a light backing on dark grounds. */
      html[data-cy-theme='dark'] img[src*='deputy'],
      html[data-cy-theme='dark'] img[src*='myob'] {
        background: #fff;
        border-radius: 8px;
        padding: 4px 8px;
        box-sizing: content-box;
      }
    `}
  />
);

/** Runs before first paint (see gatsby-ssr) so a saved theme doesn't flash. */
// The site ships white only (Frank, Sept 2026). The toggle isn't rendered, so
// clear any cream/dark choice saved during review rather than restoring it.
// To bring the review toggle back, restore the old script:
//   read STORAGE_KEY and set data-cy-theme when it's 'cream' or 'dark'.
export const themeBootScript = `try{localStorage.removeItem('${STORAGE_KEY}')}catch(e){}`;

/**
 * Review tool: switch the whole site between white, cream and dark.
 * The choice is remembered per browser.
 */
export const ThemeToggle: React.FC = () => {
  const [theme, setTheme] = React.useState<ThemeName>('white');

  React.useEffect(() => {
    const current = document.documentElement.getAttribute('data-cy-theme');
    if (current === 'cream' || current === 'dark') setTheme(current);
  }, []);

  const choose = (t: ThemeName) => {
    setTheme(t);
    if (t === 'white')
      document.documentElement.removeAttribute('data-cy-theme');
    else document.documentElement.setAttribute('data-cy-theme', t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // Private mode: the choice just won't persist.
    }
  };

  const swatch: Record<ThemeName, string> = {
    white: '#FFFFFF',
    cream: '#FFFCEB',
    dark: '#0B0F1A',
  };

  // Review-only control: collapsed to a small chip so it never covers page
  // content; opens to the three themes on click.
  const [open, setOpen] = React.useState(false);

  return (
    <Flex
      position="fixed"
      left="16px"
      // Sits above the support page's mobile "Help topics" button.
      bottom={{ base: '76px', lg: '16px' }}
      zIndex={80}
      align="center"
      gap="4px"
      p="4px"
      bg="#0E1220"
      borderRadius="999px"
      boxShadow="0 6px 20px rgba(14,18,32,.25)"
      fontFamily="'Figtree', system-ui, sans-serif"
    >
      <Flex
        as="button"
        type="button"
        aria-expanded={open}
        aria-label={open ? 'Hide theme options' : 'Preview site theme'}
        onClick={() => setOpen((o) => !o)}
        align="center"
        gap="6px"
        h="32px"
        px="10px"
        borderRadius="999px"
        fontSize="12px"
        fontWeight={700}
        color="#B4BAC5"
      >
        <Box
          as="span"
          w="12px"
          h="12px"
          borderRadius="50%"
          bg={swatch[theme]}
          border="1.5px solid #FFFFFF"
        />
        {open ? '×' : 'Theme'}
      </Flex>
      {open && (
        <Flex role="radiogroup" aria-label="Site theme" gap="4px">
          {NAMES.map((n) => {
            const on = n === theme;
            return (
              <Flex
                as="button"
                type="button"
                role="radio"
                aria-checked={on}
                key={n}
                onClick={() => choose(n)}
                align="center"
                gap="6px"
                px="12px"
                h="32px"
                borderRadius="999px"
                fontSize="13px"
                fontWeight={700}
                textTransform="capitalize"
                bg={on ? '#2667FF' : 'transparent'}
                color="#FFFFFF"
              >
                <Box
                  as="span"
                  w="12px"
                  h="12px"
                  borderRadius="50%"
                  bg={swatch[n]}
                  border="1.5px solid #FFFFFF"
                />
                {n}
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};
